import {
  Badge,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types";
import { ScriptPlatformBadge } from "@/features/persona-opportunities/components/platform/ScriptPlatformBadge";

import type {
  PersonaBriefAiEditCell,
  PersonaBriefContentPatch,
  PersonaBriefDto,
} from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import { HighlightText } from "@/features/opportunities/components/HighlightText";
import {
  createPendingBriefSave,
  shouldRetainBriefSaveFailure,
} from "@/features/persona-opportunities/lib/pending-brief-save";
import { formatScriptCreatedAt } from "@/features/persona-opportunities/lib/persona-script-meta";

import { PersonaOpportunitySelectCheckbox } from "../../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";
import { PersonaBriefExpandedBody } from "./PersonaBriefExpandedBody.js";
import { PersonaScriptTitleEditor } from "./PersonaScriptTitleEditor.js";

export const PERSONA_BRIEF_CREATOR_COUNT = 1;

export interface PersonaBriefCardProps {
  brief: PersonaBriefDto;
  canEdit?: boolean;
  isSelected?: boolean;
  onToggleSelected?: () => void;
  expanded?: boolean;
  onExpandedChange?: (open: boolean) => void;
  onPatch?: (patch: PersonaBriefContentPatch) => void | Promise<unknown>;
  onToggleFavorite?: () => void;
  onOpenHistory?: () => void;
  onAiEdit?: () => void;
  isAiEditing?: boolean;
  onApplyAi?: (cellKey: string, instruction: string) => void;
  aiEditKey?: string | null;
  aiBusyKey?: string | null;
  flashKey?: string | null;
  onStartAi?: (key: string) => void;
  onStopAi?: () => void;
  searchQuery?: string;
  onSendToVendor?: () => void;
  sentMeta?: SentScriptMeta | null;
  orderHref?: string;
  onOpenOrder?: (event: import("react").MouseEvent<HTMLAnchorElement>) => void;
}

function CollapsibleReveal({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-150 ease-out motion-reduce:transition-none",
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div
        className={cn(
          "min-h-0 overflow-hidden transition-opacity duration-150 ease-out motion-reduce:transition-none",
          open ? "opacity-100" : "opacity-0",
        )}
        inert={!open}
      >
        {children}
      </div>
    </div>
  );
}

export function parsePersonaBriefAiCell(cellKey: string): PersonaBriefAiEditCell | null {
  if (cellKey === "notes") return { target: "notes" };
  if (cellKey === "preferences") return { target: "preferences" };
  if (cellKey === "concept:direction") return { target: "concept", field: "direction" };
  if (cellKey === "concept:line") return { target: "concept", field: "line" };
  if (cellKey === "cta:direction") return { target: "cta", field: "direction" };
  if (cellKey === "cta:line") return { target: "cta", field: "line" };

  const hookMatch = /^hook:(\d+):(direction|line)$/.exec(cellKey);
  if (!hookMatch) return null;
  return {
    target: "hook",
    hookIndex: Number(hookMatch[1]),
    field: hookMatch[2] as "direction" | "line",
  };
}

export function PersonaBriefCard({
  brief,
  canEdit = false,
  isSelected = false,
  onToggleSelected,
  expanded: expandedProp,
  onExpandedChange,
  onPatch,
  onToggleFavorite,
  onOpenHistory,
  onAiEdit,
  isAiEditing = false,
  onApplyAi,
  aiEditKey = null,
  aiBusyKey = null,
  flashKey = null,
  onStartAi,
  onStopAi,
  searchQuery = "",
  onSendToVendor,
  sentMeta = null,
  orderHref,
  onOpenOrder,
}: PersonaBriefCardProps) {
  const { t, i18n } = useTranslation();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = expandedProp ?? uncontrolledExpanded;
  function setExpanded(next: boolean) {
    onExpandedChange?.(next);
    if (expandedProp === undefined) setUncontrolledExpanded(next);
  }
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const pendingSaveRef = useRef(createPendingBriefSave());
  const previousEditingKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousEditingKeyRef.current;
    previousEditingKeyRef.current = editingKey;
    // Leaving the field drops the edit. A settled failure would otherwise
    // block Share until reload, with no field name in the toast.
    if (previous && previous !== editingKey) pendingSaveRef.current.clearFailure(previous);
  }, [editingKey]);
  const hookPackage = brief.hookPackage;
  const duration = brief.duration;
  const createdAtLabel = formatScriptCreatedAt(brief.createdAt, i18n.language);

  function persist(key: string, patch: PersonaBriefContentPatch): Promise<boolean> {
    if (!canEdit || !onPatch) return Promise.resolve(false);
    const job = (async () => {
      setSavingKey(key);
      try {
        await onPatch(patch);
        return true;
      } catch {
        // Mutation toast lives on the query hook. Share reads the false result.
        return false;
      } finally {
        setSavingKey((current) => (current === key ? null : current));
      }
    })();
    pendingSaveRef.current.track(key, job, {
      retainFailure: shouldRetainBriefSaveFailure(key),
    });
    return job;
  }

  return (
    <article
      className={cn(
        "group/brief overflow-hidden rounded-md border bg-background",
        isSelected ? "border-primary bg-primary/5" : "border-border",
      )}
      data-testid="persona-brief-card"
    >
      <header
        className="flex cursor-pointer items-start gap-[11px] px-4 py-3.5"
        onClick={() => setExpanded(!expanded)}
      >
        {onToggleSelected ? (
          <PersonaOpportunitySelectCheckbox
            checked={isSelected}
            onToggle={onToggleSelected}
            label={t("personaOpportunities.scripts.briefs.selectBrief", "Select brief")}
            className="mt-px shrink-0"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <Badge
              variant="ai"
              iconStart="file-text"
              className="mt-px gap-[5px] px-2 py-0.5 text-[10.5px] font-medium tracking-[0.04em] uppercase [&>svg]:size-[11px]"
            >
              {t("personaOpportunities.scripts.briefs.badgeShort", "BRIEF")}
            </Badge>
            <ScriptPlatformBadge format={brief.youtubeFormat} />
            <button
              type="button"
              aria-pressed={brief.isFavorite}
              aria-label={
                brief.isFavorite
                  ? t(
                      "personaOpportunities.scripts.briefs.unfavoriteAria",
                      "Remove from favorites",
                    )
                  : t("personaOpportunities.scripts.briefs.favoriteAria", "Add to favorites")
              }
              onClick={(event) => {
                event.stopPropagation();
                if (!canEdit) return;
                onToggleFavorite?.();
              }}
              className={cn(
                "mt-px inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
                brief.isFavorite
                  ? "text-warning"
                  : "text-muted-foreground hover:text-warning",
              )}
            >
              <Icon
                name="star"
                size="sm"
                color="inherit"
                className={brief.isFavorite ? "fill-warning text-warning" : undefined}
                aria-hidden
              />
            </button>
            <div className="min-w-0 flex-1" onClick={(event) => event.stopPropagation()}>
              <PersonaScriptTitleEditor
                title={brief.videoName}
                canEdit={canEdit}
                highlightQuery={searchQuery}
                editAria={t(
                  "personaOpportunities.scripts.briefs.editTitleAria",
                  "Edit brief title",
                )}
                isEditing={editingKey === "title"}
                isSaving={savingKey === "title"}
                onStartEdit={() => {
                  onStopAi?.();
                  setEditingKey("title");
                }}
                onStopEdit={() =>
                  setEditingKey((current) => (current === "title" ? null : current))
                }
                onSave={(next) => persist("title", { target: "videoName", value: next })}
              />
            </div>
          </div>
          <p className="mt-[3px] text-xs italic text-muted-foreground">
            {t("personaOpportunities.scripts.briefs.kindMeta", {
              defaultValue_one: "Creator brief · {{hooks}} hook",
              defaultValue_other: "Creator brief · {{hooks}} hooks",
              count: hookPackage,
              hooks: hookPackage,
            })}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] tracking-[-0.01em] tabular-nums text-muted-foreground">
            <HighlightText
              text={t("personaOpportunities.scripts.briefs.meta", {
                defaultValue_one: "{{hooks}} hook · {{creators}} creator · {{duration}}",
                defaultValue_other: "{{hooks}} hooks · {{creators}} creator · {{duration}}",
                count: hookPackage,
                duration,
                hooks: hookPackage,
                creators: PERSONA_BRIEF_CREATOR_COUNT,
              })}
              query={searchQuery}
            />
            {createdAtLabel ? (
              <span className="inline-flex items-center gap-1">
                <Icon name="clock" size="xs" color="muted" aria-hidden />
                {createdAtLabel}
              </span>
            ) : null}
          </p>
        </div>
        {brief.version > 1 ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t(
                    "personaOpportunities.scripts.history.aria",
                    "Version history",
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditingKey(null);
                    onStopAi?.();
                    onOpenHistory?.();
                  }}
                  className="mt-[3px] inline-flex h-[26px] min-h-11 shrink-0 items-center gap-[5px] rounded-sm border border-border bg-background px-2 text-[11px] tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-[26px]"
                >
                  <Icon name="history" size="sm" color="inherit" aria-hidden />
                  {brief.version}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("personaOpportunities.scripts.history.aria", "Version history")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("personaOpportunities.scripts.briefs.collapse", {
                  defaultValue: "Collapse {{title}}",
                  title: brief.videoName,
                })
              : t("personaOpportunities.scripts.briefs.expand", {
                  defaultValue: "Expand {{title}}",
                  title: brief.videoName,
                })
          }
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(!expanded);
          }}
          className="mt-[3px] inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-sm hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0"
        >
          <Icon
            name="chevron-right"
            size="sm"
            className={cn(
              "text-muted-foreground/70 transition-transform duration-150 ease-out motion-reduce:transition-none",
              expanded && "rotate-90",
            )}
            aria-hidden
          />
        </button>
      </header>

      <CollapsibleReveal open={expanded}>
        <PersonaBriefExpandedBody
          brief={brief}
          canEdit={canEdit}
          searchQuery={searchQuery}
          editingKey={editingKey}
          savingKey={savingKey}
          aiEditKey={aiEditKey}
          aiBusyKey={aiBusyKey}
          flashKey={flashKey}
          onStartEdit={setEditingKey}
          onStopEdit={(key) => setEditingKey((current) => (current === key ? null : current))}
          onStartAi={(key) => {
            setEditingKey(null);
            onStartAi?.(key);
          }}
          onStopAi={onStopAi}
          onApplyAi={onApplyAi}
          onPersist={(key, patch) => persist(key, patch)}
          onDiscardSave={(key) => pendingSaveRef.current.clearFailure(key)}
          beforeShare={() => pendingSaveRef.current.wait()}
          isAiEditing={isAiEditing}
          onAiEdit={onAiEdit}
          onSendToVendor={onSendToVendor}
          sentMeta={sentMeta}
          orderHref={orderHref}
          onOpenOrder={onOpenOrder}
        />
      </CollapsibleReveal>
    </article>
  );
}
